import '@fontsource/poppins/latin-400.css'
import '@fontsource/poppins/latin-500.css'
import '@fontsource/poppins/latin-600.css'
import '@fontsource/sora/latin-400.css'
import '@fontsource/sora/latin-500.css'
import '@fontsource/sora/latin-600.css'
import ThemeInterface, { BrandHeading, BrandTextSize } from '@/theme-interface'

type Weight = keyof ThemeInterface['brandTypography']['weights']

/**
 * JSS for a heading of the brand type scale (Sora), including its mobile size - below 768 px window width,
 * the breakpoint of the layouts of the new design (not the device width).
 * Usage: `title: { ...brandHeading(theme, 'h5') }`
 */
export const brandHeading = (theme: ThemeInterface, level: BrandHeading, weight: Weight = 'medium') => {
  const { desktop, mobile } = theme.brandTypography.headings[level]
  return {
    fontFamily: theme.brandFontFamilies.heading,
    fontWeight: theme.brandTypography.weights[weight],
    ...desktop,
    [theme.breakpoints.down(768)]: mobile,
  }
}

/**
 * JSS for body text of the brand type scale (Poppins).
 * Usage: `hint: { ...brandText(theme, 'tiny') }`
 */
export const brandText = (theme: ThemeInterface, size: BrandTextSize, weight: Weight = 'normal') => ({
  fontFamily: theme.brandFontFamilies.body,
  fontWeight: theme.brandTypography.weights[weight],
  ...theme.brandTypography.text[size],
})

/**
 * JSS for a container that switches everything inside to the brand typefaces: Poppins for text,
 * Sora for headings and elements marked with data-typography='heading'.
 * Controls set their own font, so they are told to inherit it. :where() keeps the specificity at
 * zero, so a type scale set by the components inside always wins.
 * MUI form controls take their font from the theme typography with a class selector, which beats a
 * zero-specificity rule - they are switched explicitly.
 * Usage: `container: { ...brandFontScope(theme) }`
 */
// jss-plugin-nested splits at every comma, so each selector carries its own `&`.
// A literal (not built with map/join): a computed key of type `string` gives the returned object an
// index signature, which makes makeStyles lose its contextual typing for the spreading rule.
const MUI_FORM_CONTROLS = '& .MuiInputBase-root, & .MuiFormLabel-root, & .MuiFormHelperText-root, & .MuiFormControlLabel-label, & .MuiButton-root'

export const brandFontScope = (theme: ThemeInterface) => ({
  fontFamily: theme.brandFontFamilies.body,
  ':where(&) *': {
    fontFamily: 'inherit',
  },
  [MUI_FORM_CONTROLS]: {
    fontFamily: 'inherit',
  },
  ':where(&) :where(h1, h2, h3, h4, h5, h6, [data-typography="heading"])': {
    fontFamily: theme.brandFontFamilies.heading,
  },
})
